import { ApiError, api, type RecordingDto } from '../api.js';
import { clear, el, formatBytes, formatDateTime, formatDuration, renderEmptyState, toast } from '../ui.js';

/** Recordings list: thumbnails, inline playback, rename, download, delete. */

const STATUS_NOTES: Record<string, string> = {
  ended_early: 'Ended early',
  recording: 'In progress',
};

const END_REASON_NOTES: Record<string, string> = {
  stream_unavailable: 'the camera stream dropped',
  low_disk_space: 'the disk was nearly full',
  server_shutdown: 'the server restarted',
  server_restarted_during_recording: 'the server restarted',
  max_duration_reached: 'the time limit was reached',
  no_data: 'no video was captured',
  finalize_failed: 'the file could not be finished',
};

const buildThumbnail = (recording: RecordingDto): HTMLElement => {
  if (recording.thumbnailUrl === null) {
    return el('div', { class: 'card__thumb card__thumb--empty', 'aria-hidden': 'true' }, ['🎥']);
  }
  return el('img', {
    class: 'card__thumb',
    src: recording.thumbnailUrl,
    alt: '',
    loading: 'lazy',
    decoding: 'async',
  });
};

const subtitleFor = (recording: RecordingDto): string => {
  const parts = [
    formatDateTime(recording.startedAt),
    formatDuration(recording.durationSeconds),
    formatBytes(recording.sizeBytes),
  ];
  const note = STATUS_NOTES[recording.status];
  if (note !== undefined) {
    const reason = recording.endReason === null ? undefined : END_REASON_NOTES[recording.endReason];
    parts.push(reason === undefined ? note : `${note} — ${reason}`);
  }
  return parts.join(' · ');
};

type CardCallbacks = {
  readonly onChanged: () => void;
};

const buildCard = (recording: RecordingDto, callbacks: CardCallbacks): HTMLElement => {
  // textContent everywhere: labels are user-supplied and must never be markup.
  const title = el('p', { class: 'card__title' }, [recording.label]);
  const subtitle = el('p', { class: 'card__subtitle' }, [subtitleFor(recording)]);
  const playerSlot = el('div', { class: 'card__player' });

  const thumbButton = el('button', { class: 'card__thumb-button', type: 'button' }, [
    buildThumbnail(recording),
  ]);

  if (recording.fileUrl === null) {
    thumbButton.disabled = true;
  } else {
    thumbButton.addEventListener('click', () => {
      if (playerSlot.childElementCount > 0) {
        clear(playerSlot);
        return;
      }
      const video = el('video', {
        class: 'card__video',
        controls: 'controls',
        playsinline: 'playsinline',
        preload: 'metadata',
        src: recording.fileUrl ?? '',
      });
      playerSlot.append(video);
      void video.play().catch(() => undefined);
    });
  }

  const actions = el('div', { class: 'card__actions' });

  if (recording.downloadUrl !== null) {
    actions.append(
      el('a', { class: 'button button--ghost', href: recording.downloadUrl, download: '' }, [
        'Download',
      ]),
    );
  }

  const renameButton = el('button', { class: 'button button--ghost', type: 'button' }, ['Rename']);
  renameButton.addEventListener('click', () => {
    const next = window.prompt('Name this recording', recording.label);
    if (next === null) return;
    const trimmed = next.trim();
    if (trimmed === '' || trimmed === recording.label) return;

    void (async () => {
      try {
        await api.renameRecording(recording.id, trimmed);
        callbacks.onChanged();
      } catch (err) {
        toast(err instanceof ApiError ? err.message : 'Could not rename that recording.', 'error');
      }
    })();
  });

  const deleteButton = el('button', { class: 'button button--danger', type: 'button' }, ['Delete']);
  deleteButton.addEventListener('click', () => {
    if (!window.confirm(`Delete "${recording.label}"? This cannot be undone.`)) return;

    void (async () => {
      try {
        await api.deleteRecording(recording.id);
        toast('Recording deleted.');
        callbacks.onChanged();
      } catch (err) {
        toast(err instanceof ApiError ? err.message : 'Could not delete that recording.', 'error');
      }
    })();
  });

  actions.append(renameButton, deleteButton);

  return el('article', { class: 'card' }, [
    thumbButton,
    el('div', { class: 'card__body' }, [title, subtitle, playerSlot, actions]),
  ]);
};

const renderDiskBanner = async (host: HTMLElement): Promise<void> => {
  try {
    const disk = await api.disk();
    const summary = `${formatBytes(disk.freeBytes)} free · ${formatBytes(disk.recordingsBytes)} of recordings`;
    clear(host);
    host.append(el('p', { class: disk.low ? 'banner banner--warn' : 'banner' }, [
      disk.low ? `Low disk space — ${summary}` : summary,
    ]));
  } catch {
    clear(host);
  }
};

export const renderRecordings = (root: HTMLElement): (() => void) => {
  clear(root);

  const diskHost = el('div');
  const list = el('div', { class: 'card-list' });
  root.append(el('section', { class: 'recordings' }, [diskHost, list]));

  const load = (): void => {
    void (async () => {
      try {
        const recordings = await api.recordings();
        clear(list);

        if (recordings.length === 0) {
          renderEmptyState(
            list,
            'No recordings yet',
            'Tap “Start recording” on the Live tab to capture the camera.',
          );
          return;
        }

        for (const recording of recordings) {
          list.append(buildCard(recording, { onChanged: load }));
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) throw err;
        renderEmptyState(list, 'Could not load recordings', 'Please try again in a moment.');
      }
      void renderDiskBanner(diskHost);
    })();
  };

  load();
  return () => undefined;
};
